import React, { useEffect, useState } from 'react';
import { api, isSpendApprovalError } from './api';
import { commsApi } from './phase567Api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { quietHoursDetail, confirmQuietHours } from './quietHours';
import { blockText } from './sendStatus';
import { formatPhone } from './shared/phone';
import useContactSearch from './shared/useContactSearch';
import DialogShell from './kit/DialogShell';
import CRMRecipientPicker from './CRMRecipientPicker';
import DraftActions from './DraftActions';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Send, Users, X } from 'lucide-react';
import { splitEmailCopy, textToHtml, fillMergeFields, hasPlaceholders, defaultSendTime, scheduleIso, MERGE_HINT } from './studioSend';
import { formatDateTime } from './kit/time';

const SMS_SEGMENT = 160;
const primaryPhone = (c) => c?.phones?.find((p) => p.is_primary)?.value || c?.phones?.[0]?.value || '';

// Studio copy → a real send. Email goes through the bulk email endpoint (suppression list, consent and
// GHL DND checks run there); a text goes through the same conversation endpoint the Inbox uses (DND,
// quiet hours and the spend gate run there). Nothing here sends without the LO pressing Send.
export default function StudioSendModal({ open, onClose, onSent, channel, output }) {
  const isEmail = channel === 'email';
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [contact, setContact] = useState(null);
  const [blocked, setBlocked] = useState('');
  const [later, setLater] = useState(false);
  const [when, setWhen] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const search = useContactSearch(query, { enabled: open && !isEmail && !contact });

  useEffect(() => {
    if (!open) return;
    const split = isEmail ? splitEmailCopy(output) : { subject: '', body: output || '' };
    setSubject(split.subject);
    setBody(split.body);
    setLater(false); setWhen(defaultSendTime());
    setRecipients([]); setContact(null); setQuery(''); setBlocked(''); setError('');
  }, [open, isEmail, output]);

  const choose = async (c) => {
    setContact(c); setBlocked('');
    try {
      const status = await commsApi.sendStatus(c.contact_id);
      setBlocked(blockText(status, 'sms') || '');
    } catch { /* the server gates the send regardless */ }
  };

  const placeholders = hasPlaceholders(body) || hasPlaceholders(subject);
  // A text is merged here, not on the server, so a misspelled {{token}} would go out as typed.
  const badToken = !isEmail && /\{\{[^}]*\}\}/.test(fillMergeFields(body, { name: contact?.display_name }));
  const sendAt = isEmail && later ? scheduleIso(when) : null;
  const ready = body.trim() && !placeholders && !badToken && !sending
    && (isEmail ? subject.trim() && recipients.length > 0 && (!later || sendAt) : contact && primaryPhone(contact) && !blocked);

  const sendEmail = async () => {
    const { data } = await api.post('/email/send-bulk', {
      recipients: recipients.map((r) => ({ email: r.email, name: r.name || null })),
      subject: subject.trim(),
      html: textToHtml(body),
      text: body,
      campaign_id: 'studio',
      ...(sendAt ? { send_at: sendAt } : {}),
    });
    if (data.scheduled) {
      toast({ title: 'Email scheduled', description: `Goes to ${recipients.length} ${recipients.length === 1 ? 'person' : 'people'} on ${formatDateTime(data.send_at)}.` });
      return;
    }
    const { sent = 0, blocked = 0, failed = 0 } = data;
    const extra = [blocked && `${blocked} skipped (opted out or suppressed)`, failed && `${failed} failed`].filter(Boolean).join(', ');
    toast({ title: `Email sent to ${sent} ${sent === 1 ? 'person' : 'people'}`, description: extra || undefined });
  };

  const sendText = async () => {
    const conv = await commsApi.createConversation(contact.contact_id, 'sms');
    const payload = { channel: 'sms', body: fillMergeFields(body, { name: contact.display_name, email: contact.emails?.[0]?.value }) };
    try {
      await commsApi.sendMessage(conv.conversation_id, payload);
    } catch (e) {
      // Ticket 171: outside the send window the server refuses until the LO confirms.
      const qh = quietHoursDetail(e);
      if (!qh) throw e;
      if (!(await confirmQuietHours(qh))) return false;
      await commsApi.sendMessage(conv.conversation_id, { ...payload, confirm_quiet_hours: true });
    }
    toast({ title: 'Text sent' });
    return true;
  };

  const send = async () => {
    if (!ready) return;
    setSending(true); setError('');
    try {
      if (isEmail) await sendEmail();
      else if (!(await sendText())) return;
      onSent?.();
      onClose();
    } catch (e) {
      if (isSpendApprovalError(e)) return;
      setError(errorMessage(e, 'Send failed'));
    } finally { setSending(false); }
  };

  const smsLong = !isEmail && body.length > SMS_SEGMENT;

  return (
    <>
      <DialogShell
        open={open}
        onOpenChange={(o) => { if (!o) onClose(); }}
        title={isEmail ? 'Send this email' : 'Send this text'}
        description={isEmail ? 'Review the copy, pick who gets it, then send.' : 'Pick one contact and send. Texts follow quiet hours and opt-outs.'}
        size="lg"
        data-testid="studio-send-modal"
        footer={(
          <>
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
            <button type="button" onClick={send} disabled={!ready} data-testid="studio-send-confirm" className="btn-gold">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {later && isEmail ? 'Schedule' : 'Send'}
            </button>
          </>
        )}
      >
        <div className="space-y-3">
          {isEmail ? (
            <div>
              <Label>To</Label>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {recipients.map((r) => (
                  <span key={r.email} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs">
                    {r.name ? `${r.name} · ` : ''}{r.email}
                    <button type="button" aria-label={`Remove ${r.email}`} onClick={() => setRecipients((l) => l.filter((x) => x.email !== r.email))}><X size={11} /></button>
                  </span>
                ))}
                <button type="button" onClick={() => setPicking(true)} data-testid="studio-pick-recipients" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: '#6BA82E' }}>
                  <Users size={13} /> {recipients.length ? 'Change recipients' : 'Pick recipients'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <Label>To</Label>
              {contact ? (
                <div className="mt-1 flex items-center gap-2 text-sm" data-testid="studio-sms-contact">
                  <span className="font-semibold">{contact.display_name}</span>
                  <span className="text-slate-500">{formatPhone(primaryPhone(contact)) || 'No phone number on file'}</span>
                  <button type="button" aria-label="Change contact" onClick={() => { setContact(null); setBlocked(''); }}><X size={13} /></button>
                </div>
              ) : (
                <>
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search contacts by name or phone" className="mt-1" data-testid="studio-sms-search" />
                  <div className="mt-1 max-h-40 overflow-y-auto">
                    {search.items.map((c) => (
                      <button type="button" key={c.contact_id} onClick={() => choose(c)} className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                        {c.display_name} <span className="text-slate-400">{formatPhone(primaryPhone(c))}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {blocked && <p className="mt-1 text-xs text-red-600" data-testid="studio-sms-blocked">Can't text this contact: {blocked}</p>}
            </div>
          )}

          {isEmail && (
            <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} className="mt-1" data-testid="studio-subject" /></div>
          )}
          {isEmail && (
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={later} onChange={(e) => setLater(e.target.checked)} data-testid="studio-send-later" /> Send later
              </label>
              {later && (
                <>
                  <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1 w-64" data-testid="studio-send-at" />
                  {!sendAt && <p className="mt-1 text-xs text-amber-700" data-testid="studio-when-warning">Pick a time at least a couple of minutes from now.</p>}
                </>
              )}
            </div>
          )}
          <div>
            <Label>{isEmail ? 'Message' : 'Text'}</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={isEmail ? 10 : 4} className="mt-1" data-testid="studio-body" />
            <p className="mt-1 text-xs text-slate-500">
              {MERGE_HINT}.{smsLong ? ` ${body.length} characters: this goes out as more than one text.` : ''}
            </p>
            {isEmail && <p className="mt-1 text-xs text-slate-500" data-testid="studio-footer-note">Every email ends with your business address (Branding settings) and an unsubscribe link. They are added for you and cannot be removed.</p>}
            {isEmail && <DraftActions subject={subject} html={textToHtml(body)} subjectNote="Uses the subject above." />}
          </div>
          {badToken && <p className="text-xs text-amber-700" data-testid="studio-token-warning">{'Unknown merge field. Use only {{name}}, {{first_name}} or {{email}}.'}</p>}
          {placeholders && <p className="text-xs text-amber-700" data-testid="studio-placeholder-warning">Replace the [bracketed] text before sending.</p>}
          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
        </div>
      </DialogShell>
      {isEmail && (
        <CRMRecipientPicker open={picking} onClose={() => setPicking(false)} onConfirm={setRecipients} excludeEmails={new Set()} />
      )}
    </>
  );
}
